
import {
  defineComponent,
  computed,
  onMounted,
  onUnmounted,
  ref,
  ComponentPublicInstance,
  provide
} from "vue";
import CommonDialog from "@/components/common/CommonDialog.vue";
import RecipeDetailView from "@/components/recipes/RecipeDetailView.vue";
import IngredientsDialog from "@/components/recipes/IngredientsDialog.vue";
import MyListFolderDialog from "@/components/recipes/MyListFolderDialog.vue";
import DetailBottomBar from "@/components/recipes/DetailBottomBar.vue";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import store from "@/store";
import router from "@/router";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { isDeviceAttachment } from "@/utils/recipe-util";
import { convertRemToPx } from "@/utils/common-utils";
import {
  RequestRecipeDetail,
  RecipeDetailInfo,
  BOTTOM_ACTION_TYPE,
  LINK_TYPE
} from "@/types/recipes";
import {
  ApplianceList,
  ApplianceData,
  AttachmentList,
  ApiBaseParams,
  AttachmentData,
  RecipeComment
} from "@/types/common";
import { LocationQueryValue } from "vue-router";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
export default defineComponent({
  name: "RecipeDetailPage",
  components: {
    CommonDesignDialog,
    RecipeDetailView,
    IngredientsDialog,
    DetailBottomBar,
    MyListFolderDialog
  },
  setup() {
    const ERROR_BUTTON_LABEL = "OK";
    const { setLogId, outputPageLogs } = useLog();
    // ログIDの設定
    setLogId(PAGE_ID.OTHER);

    /** リクエスト種別 */
    const REQUEST_WEBAPI_TYPE = {
      GET_RECIPE_DETAIL: "get_recipe_detail",
      GET_FAVORITE_FOLDER: "get_favorite_folder",
      ADD_FAVORITE: "add_favorite",
      DELETE_FAVORITE: "delete_favorite",
      NONE: "none"
    };
    class ElementPostion {
      top: number;
      height: number;
      constructor() {
        this.top = -1;
        this.height = -1;
      }

      set(rect: DOMRect) {
        this.top = (rect) ? rect.top : -1;
        this.height = (rect) ? rect.height : -1; 
      }
    }

    // urlから選択されたtypeを取得
    const recipeId = Number(router.currentRoute.value.query.id);
    const recipeType = String(router.currentRoute.value.query.type);
    const debug = Boolean(router.currentRoute.value.query.debug);
    const decodeQuery = (query : string, data: LocationQueryValue | LocationQueryValue[]) => {
      NativeClient.sendDebugLog("RecipeDetailPage decodeQuery query=" + query + " data=" + data);
      let result = "";
      if (!(typeof data === "undefined")) {
        result = decodeURIComponent(String(data));
      }
      NativeClient.sendDebugLog("RecipeDetailPage decodeQuery query=" + query + " result=" + result);
      return result;
    };
    const applianceId = decodeQuery("appliance-id", router.currentRoute.value.query['appliance-id']);
    const geminiId = decodeQuery("gemini-id", router.currentRoute.value.query['gemini-id']);
    const productCode = decodeQuery("product-code", router.currentRoute.value.query['product-code']);
    const uniqueId = decodeQuery("unique-id", router.currentRoute.value.query['unique-id']);
    NativeClient.sendDebugLog("RecipeDetailPage applianceId=" + applianceId + " geminiId=" + geminiId + " productCode=" + productCode + " uniqueId=" + uniqueId);
    const detail = ref(new RecipeDetailInfo(null));
    const isLogin = ref(false);
    const selectThemeType = ref("");
    const selectVariationType = ref("");
    const recipeDetailInfoMap = new Map();
    const topTitleHeightRem = ref(0);
    const isFavoriteActive = ref(false);
    const detailViewBottom = ref(-1);
    const isShowIngredientsDialog = ref(false);
    const isShowMyListFolderDialog = ref(false);
    const isShowShareDialog = ref(false);
    const isShowFavorite = ref(false);
    const isShowBottomBar = ref(false);
    const updateDetailInfo = ref(false);
    const pageScrollTop = ref(0);
    const instructionItemTop = new Map();
    const commentTop = ref(new DOMRect(-1, -1, -1, -1));
    const detailScroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const instructionIndex = ref(0);
    const applianceList = ref(new ApplianceList());
    const targetApplianceList = ref(Array<ApplianceData>());
    const applianceData = ref(new ApplianceData());
    const attachmentList = ref(Array<AttachmentData>());
    const selectMyListFolderIds = ref(Array<number>());
    const stepNextEnable = ref(false);
    const stepPrevEnable = ref(false);
    const recipeComment = ref(new RecipeComment());

    let requestWebApiType = REQUEST_WEBAPI_TYPE.NONE;

    // 連携機器注意喚起URL
    const MY_APPLIANCE_LINK_URL =  process.env.VUE_APP_KITCHEN_APPLIANCES_URL  + "/" + "guide/device-registration.html"
    /** APIリクエスト時のエラーハンドル */
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();

    /** スクロールイベントのIF */
    interface HTMLElementEvent<T extends HTMLElement> extends Event {
      target: T;
    }
    NativeClient.sendDebugLog("RecipeDetailPage recipeId=" + recipeId + " recipeType=" + recipeType);
    targetApplianceList.value.length = 0;
    if (productCode != "" && uniqueId !== "") {
      const data = new ApplianceData();
      data.applianceId = applianceId;
      data.productCode = productCode;
      data.geminiId = geminiId;
      data.uniqueId = uniqueId;
      targetApplianceList.value.push(data);
    }
    NativeClient.sendDebugLog("RecipeDetailPage targetApplianceList=" + JSON.stringify(targetApplianceList.value));

    /** ダイアログの制御 */
    const { closeDialog } = useApiErrorDialog();

    const getThemeId = (themeName: string) => {
      let theme = null;
      if (detail.value && detail.value.info) {
        if (themeName != "" && detail.value.info.themes) {
          for (const item of detail.value.info.themes) {
            if (item.themeName == themeName) {
              theme = item.themeId;
              break;
            }
          }
        }
      }
      return theme;
    };

    const getServingsNum = (serviceUnit: string) => {
      let servings = null;
      if (detail.value && detail.value.info) {
        if (serviceUnit != "" && detail.value.info.variation) {
          for (const item of detail.value.info.variation) {
            if (item.unit == serviceUnit) {
              servings = item.num;
              break;
            }
          }
        }
      }
      return servings;
    };

    const getAttachmentApplianceId = () => {
      NativeClient.sendDebugLog("RecipeDetailPage getAttachmentApplianceId");
      const detailInfo: RecipeDetailInfo =
        store.getters["recipes/getRecipeDetail"];
      const myAppliance =
        detailInfo.info && detailInfo.info.myAppliance
          ? detailInfo.info.myAppliance
          : "";
      const isDeviceAttacment = isDeviceAttachment(myAppliance);
      if (!isDeviceAttacment) {
        return "";
      }
      let applianceId = "";
      for (const item of applianceList.value.applianceList) {
        if (!item.applianceId) {
          continue;
        }
        const attachment = isDeviceAttachment(item.productCode);
        if (attachment) {
          applianceId = item.applianceId;
          break;
        }
      }
      return applianceId;
    };

    const convertScrollPositionRem = (px: number) => {
      return px / 9;
    };

    const IMAGE_HEIGHT_REM = 28.1;
    const TITLE_HEIGHT_REM = 4.4;

    const convertPxToRem = (px: number) => {
      const fontSize = getComputedStyle(document.documentElement).fontSize;
      return px / parseFloat(fontSize);
    };

    const getInstructionNextItemPosition = (scrollTop: number) => {
      let id = -1;
      if (
        detail.value.info &&
        detail.value.info.content &&
        detail.value.info.content.instructions
      ) {
        for (let i = 0; i < instructionItemTop.size; i++) {
          const target = i;
          const rect = instructionItemTop.get(target);
          if (rect == null) {
            continue;
          }
          const itemTop = Math.round(rect.top);
          NativeClient.sendDebugLog("RecipeDetailPage getInstructionNextItemPosition itemTop=" + itemTop  + " scrollTop=" + scrollTop);
          if (scrollTop < itemTop) {
            id = target;
            break;
          }
        }
      }
      return id;
    };

    const getInstructionPrevItemPosition = (scrollTop: number) => {
      let id = -1;
      if (
        detail.value.info &&
        detail.value.info.content &&
        detail.value.info.content.instructions
      ) {
        let i = instructionItemTop.size - 1;
        while (i >= 0) {
          const target = i;
          const rect = instructionItemTop.get(target);
          if (rect == null) {
            i--;
            continue;
          }
          const itemTop = Math.round(rect.top);
          NativeClient.sendDebugLog("RecipeDetailPage getInstructionPrevItemPosition itemTop=" + itemTop  + " scrollTop=" + scrollTop);
          if (itemTop + 1 < scrollTop) {
            id = target;
            break;
          }
          i--;
        }
      }
      return id;
    };

    const getInstructionItem = (id: number) => {
      const data = new ElementPostion();
      if (
        detail.value.info &&
        detail.value.info.content &&
        detail.value.info.content.instructions
      ) { 
        const rect = instructionItemTop.get(id);
        data.set(rect);
      }
      return data;
    };

    const getInstructionNextTopPostion = (scrollTop: number) => {
      let top = -1;
      if (
        detail.value.info &&
        detail.value.info.content &&
        detail.value.info.content.instructions
      ) {
        if (instructionItemTop.size >= 1) {
          const rect = instructionItemTop.get(0);
          const itemTop = Math.round(rect.top);
          NativeClient.sendDebugLog("RecipeDetailPage getInstructionNextItemPosition itemTop=" + itemTop  + " scrollTop=" + scrollTop);
          if (scrollTop < itemTop) {
            top = itemTop;
          }
        }
      }
      return top;
    };


    const setPrevEnable = (scrollPosition: number) => {
      const prevId = getInstructionPrevItemPosition(scrollPosition);
      const prevData = getInstructionItem(prevId);
      const prevTop = prevData.top;
      NativeClient.sendDebugLog("RecipeDetailPage setPrevEnable scrollPosition=" + scrollPosition + " prevId=" + prevId + " prveData=" + JSON.stringify(prevData));
      stepPrevEnable.value = (prevTop != -1);
    };

    const setNextEnable = (scrollPosition: number) => {
      if (detailScroll.value === undefined) {
        return;
      }
      const nextId = getInstructionNextItemPosition(scrollPosition);
      const nextData = getInstructionItem(nextId);
      NativeClient.sendDebugLog("RecipeDetailPage setNextEnable scrollPosition=" + scrollPosition + " nextId=" + nextId + " nextData=" + JSON.stringify(nextData));
      let nextTop = nextData.top;
      const detailRect = detailScroll.value.getBoundingClientRect();
      NativeClient.sendDebugLog("RecipeDetailPage setNextEnable detailRect=" + JSON.stringify(detailRect));
      if (detailViewBottom.value != -1 && nextData.top != -1 && nextData.height != -1) {
        const titleHeight = convertRemToPx(TITLE_HEIGHT_REM);
        const scrollMove = detailViewBottom.value - nextData.top;
        NativeClient.sendDebugLog("RecipeDetailPage setNextEnable scrollMove=" + scrollMove + " detailViewBottom.value=" + detailViewBottom.value);
        if (scrollMove <= detailRect.height - titleHeight) {
          nextTop = -1;
          const topPositon = getInstructionNextTopPostion(scrollPosition);
          if (topPositon != -1) {
            nextTop = topPositon;
          }
          const bodyHeight = detailScroll.value.scrollHeight;
          const windowHeight = window.innerHeight;
          const bottomPoint = bodyHeight - windowHeight;
          const scrollMove = Math.round(detailScroll.value.scrollTop);
          NativeClient.sendDebugLog("RecipeDetailPage setNextEnable bodyHeight=" + bodyHeight + " windowHeight=" + windowHeight + " bottomPoint=" + bottomPoint + " detailScroll.value.scrollTop=" + detailScroll.value.scrollTop + " scrollMove=" + scrollMove);
          if (scrollMove >= bottomPoint) {
            nextTop = -1;
          }
        }
      }
      stepNextEnable.value = (nextTop != -1);
    };

    const setCursorPosition = () => {
      if (detailScroll.value === undefined) {
        return;
      }
      const scrollTop = Math.round(detailScroll.value.scrollTop);
      const titleHeight = convertRemToPx(TITLE_HEIGHT_REM);
      const scrollPosition = scrollTop + titleHeight;
      NativeClient.sendDebugLog("RecipeDetailPage setCursorPosition scrollTop=" + scrollTop + " titleHeight=" + titleHeight + " scrollPosition=" + scrollPosition);
      setPrevEnable(scrollPosition);
      setNextEnable(scrollPosition);
    };

    const requestRecipeDetailFinish = () => {
      store.dispatch("common/stopLoading");
      NativeClient.sendDebugLog("[XX]RecipeDetailPage requestRecipeDetailFinish");
      requestWebApiType = REQUEST_WEBAPI_TYPE.NONE;
      pageScrollTop.value = (detailScroll.value) ? detailScroll.value.scrollTop : 0;
      NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetailFinish pageScrollTop=" + pageScrollTop.value);
      NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetailFinish recipeComment=" + JSON.stringify(recipeComment.value));
      updateDetailInfo.value = true;
      instructionItemTop.clear();
      detailViewBottom.value = -1;
      setCursorPosition();
    };

    /**
     * レシピ詳細を取得する
     */
    const requestRecipeDetailExec = async (requstRecipeId: number, requestRecipeType: string) => {
      NativeClient.sendDebugLog(
        "RecipeDetailPage requestRecipeDetailExec requstRecipeId=" +
          requstRecipeId + " requestRecipeType=" + requestRecipeType 
      );
      requestWebApiType = REQUEST_WEBAPI_TYPE.GET_RECIPE_DETAIL;
      const servings = getServingsNum(selectVariationType.value);
      const theme = getThemeId(selectThemeType.value);
      const payload: RequestRecipeDetail = {
        type: requestRecipeType,
        id: requstRecipeId,
        servings: servings,
        theme: theme,
        applianceList: null
      };
      NativeClient.sendDebugLog(
        "RecipeDetailPage requestRecipeDetailExec payload " +
          JSON.stringify(payload)
      );
      store.dispatch("common/startLoading");
      updateDetailInfo.value = false;
      attachmentList.value.length = 0;
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        const apiParams: ApiBaseParams =
          store.getters["common/getApiBaseParams"];
        NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetailExec targetApplianceList=" + JSON.stringify(targetApplianceList.value));
        if (targetApplianceList.value.length >= 1) {
          applianceList.value.applianceList.length = 0;
          payload.applianceList = Array<string>();
          for (const data of targetApplianceList.value) {
            applianceList.value.applianceList.push(data);
            payload.applianceList.push(data.productCode);
          }
        } else {
          if (
            apiParams &&
            apiParams.applianceList &&
            apiParams.applianceList.applianceList
          ) {
            applianceList.value.applianceList =
              apiParams.applianceList.applianceList;
          }
        }
        isLogin.value = (apiParams.token != "");
        NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetailExec applianceList=" + JSON.stringify(applianceList.value.applianceList));
        isShowFavorite.value = false;
        isShowBottomBar.value = false;
        Promise.all([store.dispatch("recipes/getRecipeDetail", payload)]).then(result => {
          const recipeDetailInfo: RecipeDetailInfo = store.getters["recipes/getRecipeDetail"];
            NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetail recipeDetailInfo=" + JSON.stringify(recipeDetailInfo));
            let resRecipeId = 0;
            if (recipeDetailInfo != null && recipeDetailInfo.info != null) {
              resRecipeId = recipeDetailInfo.info.recipeId;
            }
            NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetail resRecipeId=" + resRecipeId);
              isShowBottomBar.value = true;
              if (apiParams && apiParams.token) {
                isShowFavorite.value = true;
              }
              const applianceId = getAttachmentApplianceId();
              if (applianceId) {
                try {
                  Promise.all([NativeClient.getAttachmentList(applianceId)])
                    .then(result => {
                      const list: AttachmentList =
                        store.getters["common/getAttachmentList"];
                      if (list && list.attachmentList) {
                        attachmentList.value = list.attachmentList;
                      } else {
                        attachmentList.value = Array<AttachmentData>();
                      }
                      NativeClient.sendDebugLog("RecipeDetailPage requestRecipeDetail attachmentList=" + JSON.stringify(attachmentList.value));
                      requestRecipeDetailFinish();
                    })
                    .catch(result => {
                      NativeClient.sendDebugLog(
                        "RecipeDetailPage requestRecipeDetail error result=" +
                          result
                      );
                      attachmentList.value = Array<AttachmentData>();
                      requestRecipeDetailFinish();
                    });
                } catch (e) {
                  attachmentList.value = Array<AttachmentData>();
                  requestRecipeDetailFinish();
                }
              } else {
                requestRecipeDetailFinish();
              }
        });
      });
    };

    /**
     * ページ表示時のrecipeId, recipeTypeでレシピ詳細を取得する
     */
    const requestRecipeDetail = async () => {
      requestRecipeDetailExec(recipeId, recipeType);
    };

    /**
     * レスポンス内のrecipeId, recipeTypeでレシピ詳細を取得する
     */
    const requestRecipeDetailOfficial = async (detailInfo: RecipeDetailInfo) => {
      if (detailInfo != null && detailInfo.info != null) {
        requestRecipeDetailExec(detailInfo.info.recipeId, detailInfo.info.type);
      }
    };

    const updateDetailData = async (detailInfo: RecipeDetailInfo) => {
      store.dispatch("common/startLoading");
      updateDetailInfo.value = false;
      NativeClient.sendDebugLog("RecipeDetailPage updateDetailData");
      await Promise.all([store.dispatch("recipes/updateRecipeDetail", detailInfo)]).then(
      result => {
        requestRecipeDetailFinish();
      });
    };

    /**
     * レシピ詳細の取得を実行する
     */
    const execute = async () => {
      requestRecipeDetail();
    };

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(async () => {
      outputPageLogs(null);
      execute();
      const titleTag = document.querySelector("titleTag");
      NativeClient.sendDebugLog(
        "RecipeDetailPage onMounted titleTag=" +
          titleTag?.getBoundingClientRect()
      );
    });

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onUnmounted(async () => {
      // 画面から離れた場合、stateに保持したレシピ一覧をクリア
      await store.dispatch("recipes/updateRecipeDetail", null);
    });

    const getRecipeDetailMapKey = (theme: string, serving: string) => {
      const resultThemeId = getThemeId(theme);
      const themeId = resultThemeId ? resultThemeId : -1;
      const resultServingNum = getServingsNum(serving);
      const servingNum = resultServingNum ? resultServingNum : -1;
      const key = String(themeId) + ":" + String(servingNum);
      return key;
    };

    /**
     * レシピリスト取得
     */
    const recipeDetailInfo = computed(() => {
      const recipeDetailInfo: RecipeDetailInfo =
        store.getters["recipes/getRecipeDetail"];
      NativeClient.sendDebugLog(
        "RecipeDetailPage computed recipeDetail recipeDetail=" +
          JSON.stringify(recipeDetailInfo)
      );
      if (
        recipeDetailInfo &&
        recipeDetailInfo.info &&
        recipeDetailInfo.info.content
      ) {
        if (recipeDetailInfo.info.content.theme) {
          selectThemeType.value = recipeDetailInfo.info.content.theme.themeName;
        }
        if (recipeDetailInfo.info.content.servings) {
          selectVariationType.value =
            recipeDetailInfo.info.content.servings.unit;
        }
        NativeClient.sendDebugLog(
          "RecipeDetailPage computed selectThemeType=" + selectThemeType.value
        );
        NativeClient.sendDebugLog(
          "RecipeDetailPage computed selectVariationType=" +
            selectVariationType.value
        );
      }
      if (
        recipeDetailInfo &&
        recipeDetailInfo.info &&
        recipeDetailInfo.info.reactions &&
        recipeDetailInfo.info.reactions.favorite
      ) {
        isFavoriteActive.value = recipeDetailInfo.info.reactions.favorite.done;
      }
      if (
        recipeDetailInfo &&
        recipeDetailInfo.info &&
        recipeDetailInfo.info.myAppliance &&
        applianceList.value.applianceList
      ) {
        for (const item of applianceList.value.applianceList) {
          const productCode = item.productCode;
          const hit = productCode.startsWith(recipeDetailInfo.info.myAppliance);
          if (hit) {
            applianceData.value = item;
            break;
          }
        }
      }
      detail.value = recipeDetailInfo;
      const key = getRecipeDetailMapKey(
        selectThemeType.value,
        selectVariationType.value
      );
      recipeDetailInfoMap.set(key, recipeDetailInfo);
      return recipeDetailInfo;
    });

    let saveFoloderIds = new Array<number>();
    const requestAddRecipeMyList = async (
      recipeId: number,
      folderIds: Array<number>
    ) => {
      requestWebApiType = REQUEST_WEBAPI_TYPE.ADD_FAVORITE;
      saveFoloderIds = folderIds;
      store.dispatch("common/startLoading");
      NativeClient.sendDebugLog(
        "RecipeDetailPage requestAddRecipeMyList recipeId=" +
          recipeId +
          " folderIds=" +
          JSON.stringify(folderIds)
      );
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        const apiParams: ApiBaseParams =
          store.getters["common/getApiBaseParams"];
        Promise.all([
          store.dispatch("recipes/addRecipeMyList", {
            recipeId: recipeId,
            folderIds: folderIds
          })
        ]).then(result => {
          store.dispatch("common/stopLoading");
        });
      });
    };

    const requestDeleteRecipeMyList = async (recipeId: number) => {
      requestWebApiType = REQUEST_WEBAPI_TYPE.DELETE_FAVORITE;
      NativeClient.sendDebugLog(
        "RecipeDetailPage requestDeleteRecipeMyList recipeId=" + recipeId
      );
      store.dispatch("common/startLoading");
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        const apiParams: ApiBaseParams =
          store.getters["common/getApiBaseParams"];
        Promise.all([
          store.dispatch("recipes/deleteRecipeMyList", recipeId)
        ]).then(result => {
          store.dispatch("common/stopLoading");
        });
      });
    };
    const getFoloerList = async () => {
      store.dispatch("common/startLoading");
      requestWebApiType = REQUEST_WEBAPI_TYPE.GET_FAVORITE_FOLDER;
      await Promise.all([
        NativeClient.getApiParams()
      ]).then(result => {
        Promise.all([
          store.dispatch("recipes/getFolderList"),
          store.dispatch("recipes/getRecentMyList")
        ]).then( result => {
          requestWebApiType = REQUEST_WEBAPI_TYPE.NONE;
          isShowMyListFolderDialog.value = true;
          store.dispatch("common/stopLoading");
        });
      });
    };
    /**
     * レシピ取得APIの呼び出しをリトライする
     */
    // const retryGetRecipes = () => {
    //   NativeClient.sendDebugLog("RecipeDetailPage retryGetRecipes requestWebApiType=" + requestWebApiType);
    //   // ダイアログを閉じる
    //   closeDialog();
    //   switch(requestWebApiType) {
    //     case REQUEST_WEBAPI_TYPE.GET_RECIPE_DETAIL:
    //       requestRecipeDetail();
    //       break;
    //     case REQUEST_WEBAPI_TYPE.ADD_FAVORITE:
    //       if (recipeDetailInfo.value.info && recipeDetailInfo.value.info.recipeId) {
    //         const recipeId = recipeDetailInfo.value.info.recipeId;
    //         requestAddRecipeMyList(recipeId, saveFoloderIds);
    //       }
    //       break;
    //     case REQUEST_WEBAPI_TYPE.DELETE_FAVORITE:
    //       if (recipeDetailInfo.value.info && recipeDetailInfo.value.info.recipeId) {
    //         const recipeId = recipeDetailInfo.value.info.recipeId;
    //         requestDeleteRecipeMyList(recipeId);
    //       }
    //       break;
    //     case REQUEST_WEBAPI_TYPE.GET_FAVORITE_FOLDER:
    //       getFoloerList();
    //       break;
    //   }
    //   requestWebApiType = REQUEST_WEBAPI_TYPE.NONE;
    // };
    const isShowRecipeDetail = () => {
      return (
        detail.value.info &&
        detail.value.info.content &&
        detail.value.info.title
      );
    };

    const clickTheme = (type: string) => {
      NativeClient.sendDebugLog("clickTheme type=" + type);
      if (selectThemeType.value == type) {
        return;
      }
      selectThemeType.value = type;
      const key = getRecipeDetailMapKey(
        selectThemeType.value,
        selectVariationType.value
      );
      const detailInfo = recipeDetailInfoMap.get(key);
      if (detailInfo) {
        updateDetailData(detailInfo);
      } else {
        requestRecipeDetailOfficial(detail.value);
      }
    };

    const clickVariation = (type: string) => {
      NativeClient.sendDebugLog("clickVariation type=" + type);
      if (selectVariationType.value == type) {
        return;
      }
      selectVariationType.value = type;
      const key = getRecipeDetailMapKey(
        selectThemeType.value,
        selectVariationType.value
      );
      const detailInfo = recipeDetailInfoMap.get(key);
      if (detailInfo) {
        updateDetailData(detailInfo);
      } else {
        requestRecipeDetailOfficial(detail.value);
      }
    };

    /**
     * レシピリストスクロール時イベント
     */
    const onScroll = (e: HTMLElementEvent<HTMLDivElement>) => {
      const top = convertScrollPositionRem(e.target.scrollTop);
      if (top < IMAGE_HEIGHT_REM - TITLE_HEIGHT_REM) {
        topTitleHeightRem.value = 0;
      } else if (
        top >= IMAGE_HEIGHT_REM - TITLE_HEIGHT_REM &&
        top < IMAGE_HEIGHT_REM
      ) {
        topTitleHeightRem.value = top - (IMAGE_HEIGHT_REM - TITLE_HEIGHT_REM);
        if (topTitleHeightRem.value >= IMAGE_HEIGHT_REM) {
          topTitleHeightRem.value = TITLE_HEIGHT_REM;
        }
      } else {
        topTitleHeightRem.value = TITLE_HEIGHT_REM;
      }
      setCursorPosition();
    };

    const getTopViewHeight = () => {
      return topTitleHeightRem.value + "rem";
    };

    const getFavoriteIconText = () => {
      return isFavoriteActive.value
        ? "マイページに追加済み"
        : "マイページに追加";
    };

    const setDetailViewBottomPosition = (bottom: number, pageScrollTop: number) => {
      NativeClient.sendDebugLog("RecipeDetailPage setDetailViewBottomPosition bottom=" + bottom + " pageScrollTop=" + pageScrollTop);
      detailViewBottom.value = bottom + pageScrollTop;
      setCursorPosition();
    };

    const onClickIngredientsFab = () => {
      isShowIngredientsDialog.value = true;
    };

    const closeIngredientsDialog = () => {
      isShowIngredientsDialog.value = false;
    };

    const addRecipeMyList = computed(() => {
      const result = store.getters["recipes/getAddRecipeMyList"];
      if (result != null && result.status) {
        isFavoriteActive.value = true;
        NativeClient.requestReloadMylist();
      }
      requestWebApiType = REQUEST_WEBAPI_TYPE.NONE;
      saveFoloderIds.length = 0;
    });

    const deleteRecipeMyList = computed(() => {
      const result = store.getters["recipes/getDeleteRecipeMyList"];
      if (result != null && result.status) {
        isFavoriteActive.value = false;
        NativeClient.requestReloadMylist();
      }
      requestWebApiType = REQUEST_WEBAPI_TYPE.NONE;
    });

    const onClickFarvorite = () => {
      if (
        !(recipeDetailInfo.value.info && recipeDetailInfo.value.info.recipeId)
      ) {
        NativeClient.sendDebugLog(
          "RecipeDetailPage onClickFarvorite onClickFarvorite recipeId empty"
        );
        return;
      }
      const recipeId = recipeDetailInfo.value.info.recipeId;
      if (isFavoriteActive.value) {
        requestDeleteRecipeMyList(recipeId);
      } else {
        selectMyListFolderIds.value.length = 0;
        getFoloerList();
      }
    };

    const closeMyListFolderDialog = () => {
      isShowMyListFolderDialog.value = false;
      selectMyListFolderIds.value.length = 0;
    };

    const clickAddMyList = () => {
      isShowMyListFolderDialog.value = false;
      if (
        !(recipeDetailInfo.value.info && recipeDetailInfo.value.info.recipeId)
      ) {
        selectMyListFolderIds.value.length = 0;
        NativeClient.sendDebugLog(
          "RecipeDetailPage onClickFarvorite onClickFarvorite recipeId empty"
        );
        return;
      }
      const recipeId = recipeDetailInfo.value.info.recipeId;
      requestAddRecipeMyList(recipeId, selectMyListFolderIds.value);
    };

    const selectCheck = (folderId: number, checked: boolean) => {
      NativeClient.sendDebugLog(
        "RecipeDetailPage selectCheck folderId=" + folderId + " checked=" + checked
      );
      const index = selectMyListFolderIds.value.findIndex(
        element => element == folderId
      );
      if (index == -1) {
        selectMyListFolderIds.value.push(folderId);
      } else {
        selectMyListFolderIds.value.splice(index, 1);
      }
    };

    const setInstructionItemPosition = (id: number, rect: DOMRect, pageScrollTop: number) => {
      NativeClient.sendDebugLog(
        "RecipeDetailPage setInstructionItemPosition id=" + id + " rect=" + JSON.stringify(rect) + " pageScrollTop=" + pageScrollTop
      );
      const convertRect = new DOMRect(rect.x, rect.y + pageScrollTop, rect.width, rect.height);
      instructionItemTop.set(id, convertRect);
      setCursorPosition();
    };

    const setCommentPosition = (rect: DOMRect, pageScrollTop: number) => {
      NativeClient.sendDebugLog(
        "RecipeDetailPage setCommentPosition rect=" + JSON.stringify(rect) + " pageScrollTop=" + pageScrollTop
      );
      const convertRect = new DOMRect(rect.x, rect.y + pageScrollTop, rect.width, rect.height);
      commentTop.value = convertRect;
    };

    const clickBistroSend = (dlMenuId: number) => {
      NativeClient.sendDebugLog("RecipeDetailPage clickBistroSend dlMenuId=" + dlMenuId);
      if (!dlMenuId) {
        return;
      }
      if (!(detail.value.info && applianceData.value)) {
        return;
      }
      const recipeId = detail.value.info.recipeId;
      const recipeName = detail.value.info.title;
      const myApplianceRecipe = detail.value.info.myApplianceRecipe;
      const applianceCategoryId = detail.value.info.applianceCategoryId;
      const myAppliance = detail.value.info.myAppliance;
      const groupId = detail.value.info.groupId;
      let query =
        "dlmenu-id=" +
        dlMenuId +
        "&recipe-id=" +
        recipeId +
        "&group-id=" +
        groupId +
        "&recipe-name=" +
        recipeName +
        "&appliance-category-id=" +
        applianceCategoryId +
        "&my-appliance-recipe=" +
        myApplianceRecipe +
        "&my-appliance=" +
        myAppliance;
      if (
        detail.value.info.content &&
        detail.value.info.content.servings &&
        detail.value.info.content.servings.num
      ) {
        query += "&servings=" + detail.value.info.content.servings.num;
      }
      NativeClient.sendDebugLog("RecipeDetailPage clickBistroSend query=" + query);
      location.href = "native-app://download?" + query;
    };

    const scrollNextInstructionItem = () => {
      if (detailScroll.value == null) {
        return;
      }
      const scrollTop = Math.round(detailScroll.value.scrollTop);
      NativeClient.sendDebugLog(
        "RecipeDetailPage scrollNextInstructionItem scrollTop=" + scrollTop
      );
      if (scrollTop !== undefined) {
        const titleHeight = convertRemToPx(TITLE_HEIGHT_REM);
        const nextId = getInstructionNextItemPosition(scrollTop + titleHeight);
        let nextData = getInstructionItem(nextId);
        NativeClient.sendDebugLog("RecipeDetailPage scrollNextInstructionItem nextId=" + nextId + " nextData=" + JSON.stringify(nextData));

        const topPositon = getInstructionNextTopPostion(scrollTop + titleHeight);
        if (topPositon != -1) {
          nextData = getInstructionItem(0);
        }
        if (nextData.top != -1) {
          const top = nextData.top + 1 - titleHeight;
          NativeClient.sendDebugLog("RecipeDetailPage scrollNextInstructionItem top=" + top + " titleHeight=" + titleHeight);
          detailScroll.value?.scrollTo({ top: top, left: 0 });
        }
      }
    };

    const scrollPrevInstructionItem = () => {
      if (detailScroll.value == null) {
        return;
      }
      const scrollTop = Math.round(detailScroll.value.scrollTop);
      if (scrollTop !== undefined) {
        const titleHeight = convertRemToPx(TITLE_HEIGHT_REM);
        const prevId = getInstructionPrevItemPosition(scrollTop + titleHeight);
        const prevData = getInstructionItem(prevId);
        NativeClient.sendDebugLog("RecipeDetailPage scrollPrevInstructionItem prevId=" + prevId + " prevData=" + JSON.stringify(prevData));
        if (prevData.top != -1) {
          const top = prevData.top + 1 - titleHeight;
          NativeClient.sendDebugLog("RecipeDetailPage scrollPrevInstructionItem top=" + top + " titleHeight=" + titleHeight);
          detailScroll.value?.scrollTo({ top: top, left: 0 });
        }
      }
    };

    const scrollComment = () => {
      if (detailScroll.value == null) {
        return;
      }
      const scrollTop = Math.round(detailScroll.value.scrollTop);
      if (scrollTop !== undefined) {
        const titleHeight = convertRemToPx(TITLE_HEIGHT_REM);
        NativeClient.sendDebugLog("RecipeDetailPage scrollComment commentTop.value=" + JSON.stringify(commentTop.value));
        const data = new ElementPostion();
        if (commentTop.value.top >= 0) {
          data.set(commentTop.value);
          const top = data.top + 1 - titleHeight;
          NativeClient.sendDebugLog("RecipeDetailPage scrollComment top=" + top + " titleHeight=" + titleHeight);
          detailScroll.value?.scrollTo({ top: top, left: 0 });
        }
      }
    };

    const onClickComment = () => {
      NativeClient.sendDebugLog("RecipeDetailPage onClickComment");
      scrollComment();
    };

    const clickBottomItem = (type: string) => {
      NativeClient.sendDebugLog("RecipeDetailPage clickBottomItem type=" + type);
      switch (type) {
        case BOTTOM_ACTION_TYPE.INGREDIENTS:
          onClickIngredientsFab();
          break;
        case BOTTOM_ACTION_TYPE.PREVIOUS_STEP:
          scrollPrevInstructionItem();
          break;
        case BOTTOM_ACTION_TYPE.NEXT_STEP:
          scrollNextInstructionItem();
          break;
        case BOTTOM_ACTION_TYPE.COMMENT:
          onClickComment();
          break;
      }
    };

    const closeShareDialog = () => {
      isShowShareDialog.value = false;
    };

    const clickBistroSendLink = (type: string) => {
      NativeClient.sendDebugLog("RecipeDetailPage clickBistroSendLink type=" + type);
      let url = "";
      switch (type) {
        case LINK_TYPE.SHOW_APPLIANCE_LIST:
          url = "native-app://jump-kaden";
          break;
        case LINK_TYPE.SHOW_ATTACHMENT_LIST:
          url = "native-app://jump-attachments";
          url += "?appliance-id=" + encodeURIComponent(getAttachmentApplianceId());
          break;
      }
      NativeClient.sendDebugLog("RecipeDetailPage clickBistroSendLink url=" + url);
      if (url == "") {
        return;
      }
      location.href = url;
    };

    const clickMyApplianceLink = () => {
      const url = MY_APPLIANCE_LINK_URL + "?window=browser";
      NativeClient.sendDebugLog("RecipeDetailPage clickMyApplianceLink url=" + url);
      location.href = url;
    }

    const closeError = () => {
      closeDialog();
    };

    return {
      recipeDetailInfo,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      closeDialog,
      isShowRecipeDetail,
      detail,
      selectThemeType,
      clickTheme,
      selectVariationType,
      clickVariation,
      getTopViewHeight,
      onScroll,
      getFavoriteIconText,
      isShowIngredientsDialog,
      closeIngredientsDialog,
      onClickFarvorite,
      isShowMyListFolderDialog,
      closeMyListFolderDialog,
      updateDetailInfo,
      setInstructionItemPosition,
      detailScroll,
      clickBottomItem,
      applianceData,
      clickBistroSend,
      attachmentList,
      BOTTOM_ACTION_TYPE,
      selectCheck,
      clickAddMyList,
      addRecipeMyList,
      deleteRecipeMyList,
      isShowFavorite,
      isShowBottomBar,
      isShowShareDialog,
      closeShareDialog,
      clickBistroSendLink,
      LINK_TYPE,
      stepNextEnable,
      stepPrevEnable,
      clickMyApplianceLink,
      isLogin,
      setDetailViewBottomPosition,
      setCommentPosition,
      pageScrollTop,
      recipeComment,
      closeError,
      ERROR_BUTTON_LABEL
    };
  }
});
